import JSZip from 'jszip';

/**
 * Download certificates with batching for better performance
 * Downloads certificates from AWS S3 signed URLs and creates a ZIP file
 * 
 * @param {Array} certificates - Array of certificate objects from backend
 * @param {string} eventName - Event name for ZIP file naming
 * @param {Function} onProgress - Callback for progress updates (current, total, percentage)
 * @param {number} batchSize - Number of concurrent downloads (default: 5)
 * @returns {Promise} - Resolves with download summary
 */
export const downloadCertificatesAsZipBatch = async (
  certificates,
  eventName,
  onProgress,
  batchSize = 5
) => {
  const zip = new JSZip();
  const total = certificates.length;
  let completed = 0;
  let successful = 0;
  let failed = 0;
  const errors = [];

  // Update progress
  const updateProgress = (status = 'downloading') => {
    const percentage = Math.round((completed / total) * 100);
    if (onProgress) {
      onProgress({
        current: completed,
        total,
        percentage,
        successful,
        failed,
        status
      });
    }
  };

  // Process in batches for better performance
  for (let i = 0; i < total; i += batchSize) {
    const batch = certificates.slice(i, i + batchSize);
    
    await Promise.all(
      batch.map(async (cert) => {
        try {
          const certificateUrl = cert.urlReference?.url || cert.url;
          const fileName = cert.fileName || `${cert.userId}.pdf`;

          if (!certificateUrl) {
            throw new Error('No certificate URL found');
          }

          // Fetch certificate from S3 signed URL
          const response = await fetch(certificateUrl, {
            method: 'GET',
            mode: 'cors',
            cache: 'no-cache',
            headers: {
              'Accept': 'application/pdf, */*',
            },
          });
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          const blob = await response.blob();
          zip.file(fileName, blob);
          successful++;
        } catch (error) {
          console.error(`Failed to download ${cert.fileName}:`, error);
          
          // Add error log to ZIP
          const errorFileName = `ERROR_${cert.fileName || cert.userId || 'unknown'}.txt`;
          const errorMessage = `Failed to download certificate\nUser: ${cert.userId}\nError: ${error.message}`;
          zip.file(errorFileName, errorMessage);
          
          failed++;
          errors.push({
            userId: cert.userId,
            fileName: cert.fileName,
            error: error.message
          });
        } finally {
          completed++;
          updateProgress('downloading');
        }
      })
    );
  }

  // Generate ZIP file
  updateProgress('creating_zip');
  
  const zipBlob = await zip.generateAsync({ 
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: {
      level: 6
    }
  });

  // Trigger download
  const url = URL.createObjectURL(zipBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${eventName}_certificates_${Date.now()}.zip`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return {
    total,
    successful,
    failed,
    errors
  };
};
